import { z } from 'zod';
import * as yaml from 'js-yaml';

export interface JsonToolResult {
  success: boolean;
  data?: string;
  error?: string;
  formatted?: string;
  minified?: string;
  errorPosition?: {
    line: number;
    column: number;
  };
}

export interface JsonToolOptions {
  indent?: number | '\t';
  sortKeys?: boolean;
}

const JSONInputSchema = z.string();
void JSONInputSchema;

/**
 * Format JSON with configurable indentation
 */
export function formatJson(input: string, options: JsonToolOptions = {}): JsonToolResult {
  try {
    const parsed = JSON.parse(input);
    const indent = options.indent ?? 2;
    const indentSize = indent === '\t' ? '\t' : indent;

    let result: string;
    if (options.sortKeys) {
      result = JSON.stringify(sortObjectKeys(parsed), null, indentSize);
    } else {
      result = JSON.stringify(parsed, null, indentSize);
    }

    return { success: true, data: result, formatted: result };
  } catch (e) {
    const error = e as SyntaxError;
    return { success: false, error: error.message };
  }
}

/**
 * Minify JSON by removing whitespace
 */
export function minifyJson(input: string): JsonToolResult {
  try {
    const parsed = JSON.parse(input);
    const minified = JSON.stringify(parsed);
    return { success: true, data: minified, minified };
  } catch (e) {
    const error = e as SyntaxError;
    return { success: false, error: error.message };
  }
}

/**
 * Validate JSON and return detailed error info
 */
export function validateJson(input: string): JsonToolResult {
  try {
    JSON.parse(input);
    return { success: true, data: 'Valid JSON' };
  } catch (e) {
    const error = e as SyntaxError;
    const position = extractErrorPosition(input, error.message);
    return {
      success: false,
      error: error.message,
      errorPosition: position
    };
  }
}

/**
 * Sort object keys recursively
 */
function sortObjectKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  }
  if (obj !== null && typeof obj === 'object') {
    const sorted: Record<string, unknown> = {};
    Object.keys(obj as Record<string, unknown>)
      .sort()
      .forEach((key) => {
        sorted[key] = sortObjectKeys((obj as Record<string, unknown>)[key]);
      });
    return sorted;
  }
  return obj;
}

/**
 * Extract line and column from error message
 */
function extractErrorPosition(input: string, errorMessage: string): { line: number; column: number } | undefined {
  const match = errorMessage.match(/position\s+(\d+)/i);
  if (!match) return undefined;

  const position = parseInt(match[1], 10);
  const lines = input.substring(0, position).split('\n');
  return {
    line: lines.length,
    column: lines[lines.length - 1].length + 1,
  };
}

/* -------------------------------------------------------------------------- */
/* JSON Viewer — tree representation                                            */
/* -------------------------------------------------------------------------- */

export type JsonNodeType = 'object' | 'array' | 'string' | 'number' | 'boolean' | 'null';

export interface JsonTreeNode {
  /** Stable id used as React key. */
  key: string;
  /** Property name (object) or numeric index (array). */
  label: string;
  type: JsonNodeType;
  /** Display preview (truncated for large values). */
  preview: string;
  /** Child nodes for objects/arrays. */
  children?: JsonTreeNode[];
  /** Depth in the tree (root = 0). */
  depth: number;
  /** Path from root using dotted/bracketed notation, e.g. `users[0].name`. */
  path: string;
  /** Size of the value in characters for sorting/inspection. */
  size: number;
}

const PREVIEW_LIMIT = 60;

function previewValue(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'string') {
    const truncated = value.length > PREVIEW_LIMIT ? `${value.slice(0, PREVIEW_LIMIT)}…` : value;
    return `"${truncated}"`;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) return `Array(${value.length})`;
  if (typeof value === 'object') {
    const keys = Object.keys(value as Record<string, unknown>);
    return `Object {${keys.length}}`;
  }
  return String(value);
}

function getType(value: unknown): JsonNodeType {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';
  return typeof value as JsonNodeType;
}

function buildPath(parent: string, label: string, isArrayIndex: boolean): string {
  if (parent === '') {
    return isArrayIndex ? `[${label}]` : label;
  }
  return isArrayIndex ? `${parent}[${label}]` : `${parent}.${label}`;
}

function buildTree(value: unknown, label: string, depth: number, parentPath: string, key: string, isArrayIndex: boolean): JsonTreeNode {
  const type = getType(value);
  const path = buildPath(parentPath, label, isArrayIndex);
  const size = typeof value === 'string' ? value.length : JSON.stringify(value)?.length ?? 0;

  if (type === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    const children = entries.map(([k, v], i) =>
      buildTree(v, k, depth + 1, path, `${key}-${k}-${i}`, false)
    );
    return {
      key,
      label,
      type,
      preview: previewValue(value),
      children,
      depth,
      path,
      size,
    };
  }

  if (type === 'array') {
    const arr = value as unknown[];
    const children = arr.map((item, i) =>
      buildTree(item, String(i), depth + 1, path, `${key}-${i}`, true)
    );
    return {
      key,
      label,
      type,
      preview: previewValue(value),
      children,
      depth,
      path,
      size,
    };
  }

  return {
    key,
    label,
    type,
    preview: previewValue(value),
    depth,
    path,
    size,
  };
}

export interface JsonViewerResult {
  success: boolean;
  data?: JsonTreeNode;
  error?: string;
  /** Total number of nodes in the tree (for stats). */
  nodeCount?: number;
  /** Maximum depth of the tree. */
  maxDepth?: number;
}

/**
 * Parse JSON and return a tree structure ready to render.
 */
export function buildJsonTree(input: string): JsonViewerResult {
  try {
    if (!input.trim()) {
      return { success: false, error: 'Input is empty.' };
    }
    const parsed = JSON.parse(input);
    const tree = buildTree(parsed, 'root', 0, '', 'root', false);

    let nodeCount = 0;
    let maxDepth = 0;

    const walk = (node: JsonTreeNode): void => {
      nodeCount++;
      if (node.depth > maxDepth) maxDepth = node.depth;
      node.children?.forEach(walk);
    };
    walk(tree);

    return { success: true, data: tree, nodeCount, maxDepth };
  } catch (e) {
    return { success: false, error: (e as Error).message };
  }
}

/* -------------------------------------------------------------------------- */
/* JSON ↔ YAML conversion                                                       */
/* -------------------------------------------------------------------------- */

export interface JsonYamlResult {
  success: boolean;
  data?: string;
  error?: string;
}

/**
 * Convert JSON to YAML.
 */
export function jsonToYaml(input: string, indent: number = 2): JsonYamlResult {
  try {
    const parsed = JSON.parse(input);
    const result = yaml.dump(parsed, {
      indent,
      lineWidth: -1, // do not wrap long lines
      noRefs: true,
      sortKeys: false,
    });
    return { success: true, data: result };
  } catch (e) {
    return { success: false, error: (e as Error).message };
  }
}

/**
 * Convert YAML to JSON.
 */
export function yamlToJson(input: string, indent: number = 2): JsonYamlResult {
  try {
    const parsed = yaml.load(input);
    if (parsed === undefined && input.trim().length > 0) {
      return { success: false, error: 'YAML parsed to undefined — input may be empty or invalid.' };
    }
    const result = JSON.stringify(parsed, null, indent);
    return { success: true, data: result };
  } catch (e) {
    return { success: false, error: (e as Error).message };
  }
}
